import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import './Navigation.css';
// import { useUser } from '../../../src/common/UserContext';

const Navigation = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isProfileDropdownOpen &&
        !event.target.closest('.profile-container')
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const closeProfileDropdown = () => {
    setIsProfileDropdownOpen(false);
  };

  // Don't show navigation on authentication page
  if (location.pathname === '/') {
    return null;
  }

  if (loading) {
    return (
      <nav className="navigation">
        <div className="nav-container">
          <div className="nav-loading">
            <div className="loading-spinner"></div>
            <span>Loading...</span>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navigation">
      <div className="nav-container">
        {/* Logo/Brand */}
        <div className="nav-brand">
          <button onClick={() => navigate('/dashboard')} className="brand-link">
            <span className="brand-icon">T</span>
            <span className="brand-text">Taichi Admin</span>
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="nav-links">
          <button
            onClick={() => navigate('/dashboard')}
            className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
          >
            <span className="nav-icon">≡</span>
            Dashboard
          </button>

          <button
            onClick={() => navigate('/create-post')}
            className={`nav-link ${location.pathname === '/create-post' ? 'active' : ''}`}
          >
            <span className="nav-icon">+</span>
            Create Post
          </button>
        </div>

        {/* User Profile */}
        <div className="nav-user">
          <div className="profile-container">
            <button
              onClick={toggleProfileDropdown}
              className="profile-button"
              aria-label="User profile menu"
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  {user?.displayName
                    ? user.displayName.charAt(0).toUpperCase()
                    : 'U'}
                </div>
              )}
            </button>

            {/* Profile Dropdown */}
            {isProfileDropdownOpen && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">
                      {user?.displayName || 'User'}
                    </span>
                    <span className="dropdown-user-email">{user?.email}</span>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-actions">
                  <button
                    onClick={() => {
                      handleSignOut();
                      closeProfileDropdown();
                    }}
                    className="dropdown-signout-btn"
                  >
                    <span className="dropdown-signout-icon">→</span>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn" onClick={toggleMenu}>
          <span className={`hamburger ${isMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-content">
          <div className="mobile-user-info">
            <div className="mobile-user-avatar-container">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  className="mobile-user-avatar"
                />
              ) : (
                <div className="mobile-user-avatar-placeholder">
                  {user?.displayName
                    ? user.displayName.charAt(0).toUpperCase()
                    : 'U'}
                </div>
              )}
            </div>
            <div className="mobile-user-details">
              <span className="mobile-user-name">
                {user?.displayName || 'User'}
              </span>
              <span className="mobile-user-email">{user?.email}</span>
            </div>
          </div>

          <div className="mobile-nav-links">
            <button
              onClick={() => {
                navigate('/dashboard');
                closeMenu();
              }}
              className={`mobile-nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
            >
              <span className="mobile-nav-icon">≡</span>
              Dashboard
            </button>

            <button
              onClick={() => {
                navigate('/create-post');
                closeMenu();
              }}
              className={`mobile-nav-link ${location.pathname === '/create-post' ? 'active' : ''}`}
            >
              <span className="mobile-nav-icon">+</span>
              Create Post
            </button>
          </div>

          <div className="mobile-user-actions">
            <button
              onClick={() => {
                handleSignOut();
                closeMenu();
              }}
              className="mobile-signout-btn"
            >
              <span className="mobile-signout-icon">→</span>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="mobile-overlay" onClick={closeMenu}></div>}
    </nav>
  );
};

export default Navigation;
